import api from './apiService';
import { parseConsistentApiResponse } from '../utils/consistentApiParser';

const sensorService = {
  // Get all sensors
  getAll: async () => {
    // Silent operation

    const response = await api.get('/sensors');

    // Parse the consistent API response
    const apiResponse = parseConsistentApiResponse(response, 'sensorService', 'getAll');

    // Return the data directly (should be an array of sensors)
    return apiResponse.data || [];
  },

  // Get sensor by ID
  getById: async (id) => {
    const response = await api.get(`/sensors/${id}`);

    // Parse the consistent API response
    const apiResponse = parseConsistentApiResponse(response, 'sensorService', 'getById');

    // Return the data directly (should be a single sensor object)
    return apiResponse.data;
  },

  // Create new sensor
  create: async (sensorData) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('sensorService: Creating sensor with data:', sensorData);
      }

      // Create a copy of the sensorData to avoid modifying the original
      const sensorDataToSend = { ...sensorData };

      const response = await api.post('/sensors', sensorDataToSend);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'sensorService', 'create');

      // Return the data directly (should be the created sensor object)
      return apiResponse.data;
    } catch (error) {
      // Always log errors, even in production
      console.error('sensorService: Error creating sensor');

      if (process.env.NODE_ENV === 'development') {
        console.error('sensorService: Error details:', error);
        console.error('sensorService: Error response data:', error.response?.data);
        console.error('sensorService: Error status:', error.response?.status);
      }

      // Rethrow with more detailed error message
      if (error.response?.data) {
        const errorMessage = typeof error.response.data === 'object'
          ? JSON.stringify(error.response.data)
          : error.response.data;
        throw new Error(`API Error (${error.response.status}): ${errorMessage}`);
      }
      throw error;
    }
  },

  // Update sensor
  update: async (id, sensorData) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('sensorService: Updating sensor with ID:', id, 'and data:', sensorData);
      }

      // Create a copy of the sensorData to avoid modifying the original
      const sensorDataToSend = { ...sensorData };

      const response = await api.put(`/sensors/${id}`, sensorDataToSend);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'sensorService', 'update');

      // Return the data directly (should be the updated sensor object)
      return apiResponse.data;
    } catch (error) {
      // Always log errors, even in production
      console.error('sensorService: Error updating sensor');

      if (process.env.NODE_ENV === 'development') {
        console.error('sensorService: Error details:', error);
        console.error('sensorService: Error response data:', error.response?.data);
      }

      throw error;
    }
  },

  // Delete sensor
  remove: async (id) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('sensorService: Deleting sensor with ID:', id);
      }

      const response = await api.delete(`/sensors/${id}`);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'sensorService', 'remove');

      // For delete operations, return the ID if successful
      if (apiResponse.success) {
        return id;
      }

      return apiResponse;
    } catch (error) {
      // Always log errors, even in production
      console.error('sensorService: Error deleting sensor');

      if (process.env.NODE_ENV === 'development') {
        console.error('sensorService: Error details:', error);
        console.error('sensorService: Error response data:', error.response?.data);
      }

      throw error;
    }
  },
};

export default sensorService;
